import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CageWinchMeta } from '@/domain/cageWinch';

import { isWithinLimit } from '../helpers/formatWinchValue';
import {
    type MeasurementCoords,
    resolveDisplayedMeasurement,
} from '../helpers/resolveDisplayedMeasurement';
import type { WinchMqttUser } from '../winchMqttApi';

import { type CageWinchMqtt, useCageWinchMqtt } from './useCageWinchMqtt';
import { useWinchScanUi } from './useWinchScanUi';

export const DEFAULT_WINCH_LIMITS = {
    distance: { min: 4, max: 40 },
    depth: { min: 4, max: 40 },
};

type MeasurementDraft = MeasurementCoords & {
    minDepth: number;
    maxDepth: number;
};

export type CageWinchControl = {
    winch: CageWinchMqtt;
    measurement: MeasurementCoords;
    verticalScan: { enabled: boolean; minDepth: number; maxDepth: number };
    limits: { distance: { min: number; max: number }; depth: { min: number; max: number } };
    service: { distance: number; depth: number } | null | undefined;
    isReady: boolean;
    canMoveSelected: boolean;
    canMoveService: boolean;
    moveDisabledReason: string;
    serviceMoveDisabledReason: string;
    hasEditedMeasurement: boolean;
    scanRunning: boolean;
    saveMeasurement: (values: MeasurementDraft) => void;
    toggleScan: (enabled: boolean) => void;
    moveMeasurement: () => void;
    moveToService: () => void;
    stopWinch: () => void;
};

function asCoords(
    distance: number | null | undefined,
    depth: number | null | undefined
): MeasurementCoords | null {
    if (distance == null || depth == null) {
        return null;
    }
    return { distance, depth };
}

export function useCageWinchControl(
    winchData: CageWinchMeta | null,
    user: WinchMqttUser
): CageWinchControl {
    const { t } = useTranslation();
    const winch = useCageWinchMqtt(
        winchData?.winchId ?? null,
        Boolean(winchData?.winchExists),
        user
    );

    const [draft, setDraft] = useState<MeasurementDraft | null>(null);
    const [frozenLive, setFrozenLive] = useState<MeasurementCoords | null>(null);
    const liveDistance = winch.telemetry?.distance;
    const liveDepth = winch.telemetry?.depth;
    const live = asCoords(liveDistance, liveDepth);
    const liveFallback = frozenLive ?? live;
    const saved = winch.config?.measurementPosition ?? null;
    const mqttScan = winch.verticalScan;
    const { scanRange, setScanRange, scanEnabled, setScanEnabled, scanRunning } = useWinchScanUi({
        mqttScan,
        setLimitsFailed: winch.setLimitsFailed,
    });
    const scanStarting = winch.scanStarting && winch.canMove && !scanRunning;

    const resolvedMeasurement = resolveDisplayedMeasurement({
        draft: draft ? { distance: draft.distance, depth: draft.depth } : null,
        saved,
        liveFallback,
    });
    const measurement =
        (scanEnabled || mqttScan?.enabled) && mqttScan?.x != null
            ? { ...resolvedMeasurement, distance: mqttScan.x }
            : resolvedMeasurement;

    useEffect(() => {
        if (frozenLive == null && liveDistance != null && liveDepth != null) {
            setFrozenLive({ distance: liveDistance, depth: liveDepth });
        }
    }, [frozenLive, liveDistance, liveDepth]);

    useEffect(() => {
        if (winch.setPositionFailed) {
            setDraft(null);
            return;
        }
        if (winch.setPositionPending || draft == null) {
            return;
        }
        if (saved != null && saved.distance === draft.distance && saved.depth === draft.depth) {
            setDraft(null);
        }
    }, [winch.setPositionFailed, winch.setPositionPending, draft, saved]);

    const limits = winch.limits ?? DEFAULT_WINCH_LIMITS;
    const minDepth = draft?.minDepth ?? mqttScan?.yMin ?? scanRange?.minDepth ?? limits.depth.min;
    const maxDepth = draft?.maxDepth ?? mqttScan?.yMax ?? scanRange?.maxDepth ?? limits.depth.max;
    const verticalScan = { enabled: scanEnabled, minDepth, maxDepth };
    const limitsUnconfirmed = winch.setLimitsPending || (winch.setLimitsFailed && mqttScan == null);

    const measurementInRange =
        isWithinLimit(measurement.distance, limits.distance.min, limits.distance.max) &&
        (scanEnabled
            ? isWithinLimit(minDepth, limits.depth.min, limits.depth.max) &&
              isWithinLimit(maxDepth, limits.depth.min, limits.depth.max) &&
              minDepth < maxDepth
            : isWithinLimit(measurement.depth, limits.depth.min, limits.depth.max));

    const hasUnsavedEdit = draft != null || winch.setPositionPending;
    const scanStatusMissing = scanEnabled && mqttScan == null;
    // Move sends the named preset, so the device's saved position must be known.
    const measurementPresetMissing = !scanEnabled && saved == null;
    const servicePresetMissing = winch.config?.servicePosition == null;
    const canMoveSelected =
        winch.canMove &&
        measurementInRange &&
        !hasUnsavedEdit &&
        !measurementPresetMissing &&
        !scanRunning &&
        !scanStarting &&
        !limitsUnconfirmed &&
        !scanStatusMissing;
    const canMoveService = winch.canMove && !servicePresetMissing && !scanRunning && !scanStarting;

    const winchBlockedReason = winch.moving
        ? t('winchControl.tooltip.currentlyMoving')
        : t('winchControl.tooltip.statusOffline');
    const moveDisabledReason = !winch.canMove
        ? winchBlockedReason
        : hasUnsavedEdit
          ? t('winchControl.tooltip.saveBeforeMoving')
          : measurementPresetMissing
            ? t('winchControl.tooltip.positionNotLoaded')
            : limitsUnconfirmed
              ? t('winchControl.tooltip.waitForScanLimits')
              : scanStatusMissing || scanStarting
                ? t('winchControl.tooltip.waitForScanStatus')
                : scanRunning
                  ? t('winchControl.tooltip.stopScanBeforeService')
                  : t('winchControl.tooltip.outOfRange');
    const serviceMoveDisabledReason = !winch.canMove
        ? winchBlockedReason
        : servicePresetMissing
          ? t('winchControl.tooltip.positionNotLoaded')
          : scanStarting
            ? t('winchControl.tooltip.waitForScanStatus')
            : t('winchControl.tooltip.stopScanBeforeService');

    const saveMeasurement = (values: MeasurementDraft): void => {
        if (scanEnabled) {
            const published = winch.setVerticalScanLimits({
                x: measurement.distance,
                yMin: values.minDepth,
                yMax: values.maxDepth,
            });
            if (published) {
                setScanRange({ minDepth: values.minDepth, maxDepth: values.maxDepth });
            }
            return;
        }
        setScanRange({ minDepth: values.minDepth, maxDepth: values.maxDepth });
        setDraft(values);
        winch.saveMeasurementPosition({ distance: values.distance, depth: values.depth });
    };

    const stopScan = (): void => {
        if (!winch.disableVerticalScan()) {
            return;
        }
        if (!scanRunning) {
            setScanEnabled(false);
        }
    };

    const moveToService = (): void => {
        if (!canMoveService) {
            return;
        }
        winch.moveToService();
    };

    const toggleScan = (enabled: boolean): void => {
        if (enabled) {
            setScanEnabled(true);
            return;
        }
        stopScan();
    };

    const moveMeasurement = (): void => {
        if (scanEnabled) {
            if (limitsUnconfirmed || mqttScan == null || scanStarting) {
                return;
            }
            winch.enableVerticalScan();
            return;
        }
        if (!canMoveSelected) {
            return;
        }
        winch.moveToMeasurement();
    };

    const stopWinch = (): void => {
        if (scanRunning) {
            stopScan();
            return;
        }
        winch.stop();
    };

    return {
        winch,
        measurement,
        verticalScan,
        limits,
        service: winch.config?.servicePosition,
        isReady: Boolean(winch.telemetry || winch.config),
        canMoveSelected,
        canMoveService,
        moveDisabledReason,
        serviceMoveDisabledReason,
        hasEditedMeasurement: draft != null,
        scanRunning,
        saveMeasurement,
        toggleScan,
        moveMeasurement,
        moveToService,
        stopWinch,
    };
}
